import React, { useState } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { isEmpty } from 'lodash';

import FeedFilterPanel, {
  filterOps
} from 'buyside/components/presentation/Feed/FeedFilterPanel/feedFilterPanel';
import FeedItems from 'buyside/components/presentation/Feed/FeedItems/feedItems';
import FeedItemLoading from 'buyside/components/presentation/Feed/FeedItem/feedItemLoading';
import DelayedAccessAlert, {
  Location
} from 'buyside/components/presentation/AccessControl/delayedAccessAlert';
import useInfiniteScroll from 'buyside/util/hooks/useInfiniteScroll';

import { FeedQuery, useFeedQuery, useEntityFeedCurrentUserQuery } from 'interfaces/graphql-typings';

export const feedType = 'Entity';

const EntityShowFeed: React.FC<{ id: string; isPublic: boolean }> = ({ id, isPublic }) => {
  const [filter, setFilter] = useState(filterOps[0]);

  const { data, loading, error, fetchMore } = useFeedQuery({
    variables: {
      feedId: id,
      feedType,
      feedFilter: filter
    },
    notifyOnNetworkStatusChange: true
  });

  if (error) throw error;

  const pageInfo = data?.feedItems?.pageInfo;

  const [isFetching] = useInfiniteScroll(
    Boolean(pageInfo?.hasNextPage),
    (): Promise<ApolloQueryResult<FeedQuery>> =>
      fetchMore({
        variables: {
          feedId: id,
          feedType,
          feedFilter: filter,
          cursor: pageInfo?.endCursor
        }
      })
  );

  const { data: userData, loading: userLoading, error: userError } = useEntityFeedCurrentUserQuery({
    skip: isPublic
  });

  const isLoading = loading || userLoading;
  const isLoadingMore = Boolean(pageInfo?.hasNextPage && isFetching);

  if (userError) throw error;

  return (
    <>
      {isPublic ? null : (
        <FeedFilterPanel
          feedFilter={filter}
          isLoading={isLoadingMore || isLoading}
          setFilter={(): void => setFilter(filterOps.indexOf(filter) ? filterOps[0] : filterOps[1])}
        />
      )}
      <DelayedAccessAlert id={Location.entityFeed} isPublic={isPublic} sectionName="feed" />
      {isLoading && isEmpty(data?.feedItems?.edges) ? (
        <FeedItemLoading times={3} />
      ) : (
        <FeedItems
          currentUser={userData?.currentUser}
          feedItems={data?.feedItems}
          feedType={feedType}
          isLoadingEntries={isLoadingMore}
          isPublic={isPublic}
          recommendations={null}
        />
      )}
    </>
  );
};

export default EntityShowFeed;
